import React, { useEffect, useRef, useState } from 'react';
import LoadingSpinnerImage from "../Shared/LoadingSpinnerImage.js";
import Categories from "../SystematicReviews/Categories.js";
import RowActionMenu from "./RowActionMenu.js";
import { PrimaryPillButton } from "./ActionButtons.js";

let searchDebounce = null;
const BASE_SR_URL = "/your-medboard/systematic-reviews/";

const getCategoryName = (type) => {
    const found = Categories.find((c) => c.category === type);
    return found ? found.name : (type || "—");
};

const formatLastMatch = (mostRecentContent) => {
    if (!mostRecentContent)
        return "No information yet";

    const date = new Date(mostRecentContent);
    const now = new Date();
    const isToday =
        date.getFullYear() === now.getFullYear() &&
        date.getMonth() === now.getMonth() &&
        date.getDate() === now.getDate();

    if (isToday)
        return "Today";

    let days = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (days === 0)
        days = 1;
    return days + " day" + (days > 1 ? "s" : "") + " ago";
};

const ReviewModeCell = ({ mode, mostRecentContent }) => {
    if (mode == null || mode === 1) {
        return (
            <span className="ellipsis" title="One time" style={{ cursor: "help", border: 0, textAlign: "center", fontSize: "11px", padding: "2px 0", display: "inline-flex" }}>
                <span className="g-mr-6" style={{ color: "#777", fontSize: "9px", borderRadius: "25px", border: "1px solid #dbdbdb", width: "19px", height: "19px", display: "flex", justifyContent: "center", alignItems: "center", fontWeight: "bold" }}>1</span>
                One Time
            </span>
        );
    }

    const lastMatch = formatLastMatch(mostRecentContent);
    return (
        <span title={`Last match: ${lastMatch}`} className="ellipsis" style={{ cursor: "help", border: 0, textAlign: "center", fontSize: "11px", padding: "2px 6px 2px 0", display: "inline-flex" }}>
            <i className="fas fa-sync-alt g-mr-6" style={{ color: "#777", fontSize: "9px", borderRadius: "25px", border: "1px solid #dbdbdb", width: "19px", height: "19px", display: "flex", justifyContent: "center", alignItems: "center", fontWeight: "bold" }} />
            Continuous
        </span>
    );
};

const LinkSystematicReviewsPopup = ({
    isOpen,
    onClose,
    workspaceId,
    linkedIds,
    onLinked,
}) => {
    const [searchPhrase, setSearchPhrase] = useState("");
    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [linkingId, setLinkingId] = useState(null);
    const [error, setError] = useState(null);
    const [locallyLinkedIds, setLocallyLinkedIds] = useState([]);
    const searchAbortRef = useRef(null);

    const linkedSet = new Set([
        ...(linkedIds || []).map((id) => Number(id)),
        ...locallyLinkedIds.map((id) => Number(id)),
    ]);

    const loadReviews = (search = "") => {
        if (searchAbortRef.current)
            searchAbortRef.current.abort();

        const controller = new AbortController();
        searchAbortRef.current = controller;

        setIsLoading(true);
        setError(null);
        // groupId forces the API to include all SR category aliases when not searching
        const query = search && search.trim().length >= 1
            ? `search=${encodeURIComponent(search.trim())}&page=1`
            : "page=1&groupId=0";

        fetch(`/umbraco/api/SystematicReviewApi/SystematicReviews?${query}`, { signal: controller.signal })
            .then((response) => {
                if (!response.ok) throw new Error("Could not load systematic reviews");
                return response.json();
            })
            .then((data) => {
                if (controller.signal.aborted)
                    return;
                setResults((data || []).slice(0, 30));
            })
            .catch((err) => {
                if (err?.name === "AbortError")
                    return;
                setResults([]);
                setError(err.message || "Could not load systematic reviews");
            })
            .finally(() => {
                // Keep spinner up when this request was superseded by newer typing/search
                if (controller.signal.aborted || searchAbortRef.current !== controller)
                    return;
                setIsLoading(false);
            });
    };

    useEffect(() => {
        if (!isOpen)
            return;

        setSearchPhrase("");
        setLinkingId(null);
        setError(null);
        setLocallyLinkedIds([]);
        loadReviews("");
    }, [isOpen]);

    useEffect(() => {
        return () => {
            if (searchDebounce)
                clearTimeout(searchDebounce);
            if (searchAbortRef.current)
                searchAbortRef.current.abort();
        };
    }, []);

    const onSearchChange = (value) => {
        setSearchPhrase(value);
        if (searchDebounce)
            clearTimeout(searchDebounce);

        // Cancel in-flight search but keep loading visible through continued typing
        if (searchAbortRef.current)
            searchAbortRef.current.abort();
        setIsLoading(true);
        setError(null);

        searchDebounce = setTimeout(() => {
            loadReviews(value);
        }, 400);
    };

    const linkItem = (sr) => {
        if (!sr?.Id || linkedSet.has(Number(sr.Id)) || linkingId)
            return;

        setLinkingId(sr.Id);
        setError(null);
        const formData = new FormData();
        formData.append("workspaceId", workspaceId);
        formData.append("systematicReviewId", sr.Id);

        fetch("/umbraco/api/WatchlistsApi/WatchlistSystematicReviews", { method: "POST", body: formData })
            .then(async (response) => {
                if (!response.ok) {
                    const message = await response.text();
                    throw new Error(message || "Could not link systematic review");
                }
                return response.json();
            })
            .then(() => {
                setLocallyLinkedIds((prev) => [...prev, Number(sr.Id)]);
                onLinked?.();
            })
            .catch((err) => setError(err.message))
            .finally(() => setLinkingId(null));
    };

    if (!isOpen)
        return null;

    return (
        <div
            className="watchlists-add-profiles-overlay"
            onClick={(e) => {
                if (e.target === e.currentTarget)
                    onClose?.();
            }}
        >
            <div className="add-task-popup rims-filter-container watchlists-link-sr-popup">
                <div className="rims-filter-header">
                    <h3>Link Systematic Review</h3>
                    <button
                        type="button"
                        className="close-button"
                        onClick={onClose}
                        style={{ border: "none", background: "transparent", cursor: "pointer" }}
                        aria-label="Close"
                    >
                        <i className="fas fa-times" />
                    </button>
                </div>

                <div className="rims-filter-input-wrapper" style={{ width: "100%", marginBottom: "18px" }}>
                    <input
                        className="rims-filter-input"
                        type="text"
                        autoFocus
                        value={searchPhrase}
                        onChange={(e) => onSearchChange(e.target.value)}
                        placeholder="Search systematic reviews..."
                        style={{ width: "100%" }}
                    />
                </div>

                {error && <div style={{ color: "#e53e3e", marginBottom: "12px", fontSize: "13px" }}>{error}</div>}

                {isLoading ? (
                    <div style={{ display: "flex", justifyContent: "center", padding: "24px" }}>
                        <LoadingSpinnerImage />
                    </div>
                ) : results.length === 0 ? (
                    (searchPhrase || "").trim() ? (
                        <div className="watchlists-add-profiles-banner watchlists-add-profiles-banner-hint">
                            <span>No systematic reviews found.</span>
                        </div>
                    ) : (
                        <div className="watchlists-add-profiles-banner watchlists-add-profiles-banner-hint">
                            <i className="fas fa-search" aria-hidden="true" />
                            <span>Type to search systematic reviews.</span>
                        </div>
                    )
                ) : (
                    <div className="watchlists-link-sr-list">
                        {results.map((sr) => {
                            const alreadyLinked = linkedSet.has(Number(sr.Id));
                            const isLinking = linkingId === sr.Id;
                            const typeLabel = getCategoryName(sr.Type);
                            return (
                                <div key={sr.Id} className="watchlists-link-sr-item">
                                    <div className="watchlists-link-sr-item-main">
                                        <div className="watchlists-link-sr-item-name">
                                            {sr.Title || `SR #${sr.Id}`}
                                        </div>
                                        <div className="watchlists-link-sr-item-type">{typeLabel}</div>
                                    </div>
                                    <button
                                        type="button"
                                        className="watchlists-add-profiles-add-btn"
                                        disabled={alreadyLinked || isLinking}
                                        onClick={() => linkItem(sr)}
                                    >
                                        {isLinking ? (
                                            <LoadingSpinnerImage small={true} />
                                        ) : alreadyLinked ? (
                                            "Linked"
                                        ) : (
                                            "Link"
                                        )}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

const SystematicReviewsTab = ({ workspaceId }) => {

    const [items, setItems] = useState([]);
    const [currentUserId, setCurrentUserId] = useState(window.currentUserId || null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isLinkPopupOpen, setIsLinkPopupOpen] = useState(false);
    const [openMenuId, setOpenMenuId] = useState(null);
    const [menuAnchor, setMenuAnchor] = useState(null);

    const closeActionMenu = () => {
        setOpenMenuId(null);
        setMenuAnchor(null);
    };

    const toggleActionMenu = (itemId, e) => {
        e.preventDefault();
        e.stopPropagation();
        if (openMenuId === itemId) {
            closeActionMenu();
            return;
        }
        setMenuAnchor(e.currentTarget);
        setOpenMenuId(itemId);
    };

    const loadItems = () => {
        setIsLoading(true);
        setError(null);
        fetch(`/umbraco/api/WatchlistsApi/WatchlistSystematicReviews?workspaceId=${workspaceId}`)
            .then((response) => {
                if (!response.ok) throw new Error("Could not load systematic reviews");
                return response.json();
            })
            .then((data) => {
                setItems(data || []);
                if (data?.length && data[0].CurrentUserId != null)
                    setCurrentUserId(data[0].CurrentUserId);
            })
            .catch((err) => setError(err.message))
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        loadItems();
        closeActionMenu();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workspaceId]);

    useEffect(() => {
        if (!currentUserId) {
            fetch("/umbraco/api/Compliance/CurrentUserId")
                .then((response) => response.ok ? response.json() : null)
                .then((id) => { if (id != null) setCurrentUserId(id); })
                .catch(() => {});
        }
    }, []);

    const unlinkItem = (systematicReviewId) => {
        if (!window.confirm("Remove this systematic review from the watchlist?")) return;
        closeActionMenu();
        fetch(`/umbraco/api/WatchlistsApi/WatchlistSystematicReviews?workspaceId=${workspaceId}&systematicReviewId=${systematicReviewId}`, { method: "DELETE" })
            .then((response) => {
                if (!response.ok) throw new Error("Could not unlink systematic review");
                loadItems();
            })
            .catch((err) => setError(err.message));
    };

    const getSrLink = (sr) => `${BASE_SR_URL}${sr.Type || ""}/${sr.Id}/`;

    const getNameStyle = (sr) => {
        let color = "#385898";
        let tooltip = "";
        if (sr.Private === 1) {
            tooltip = "The access to this systematic review is restricted but you have permission to access it";
            const collaborators = sr.Collaborators || [];
            const hasAccess = collaborators.some((c) => c.MemberId === currentUserId) || sr.ResponsiblePerson === currentUserId;
            if (!hasAccess) {
                color = "#8f8f8f";
                tooltip = "You do not have permission to access this systematic review";
            }
        }
        return { color, tooltip };
    };

    const getResponsible = (sr) => {
        const userId = sr.ResponsiblePersonId || sr.ResponsiblePerson || sr.CreatedBy;
        const initials = sr.ResponsiblePersonInitials || "—";
        const name = sr.ResponsiblePersonName || "";
        return {
            initials,
            name,
            isMe: userId === currentUserId || userId === sr.CurrentUserId,
        };
    };

    return (
        <div>
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginBottom: "16px" }}>
                <PrimaryPillButton onClick={() => setIsLinkPopupOpen(true)}>
                    <i className="fas fa-link pointer g-mr-8"></i>Link Systematic Review
                </PrimaryPillButton>
            </div>

            {error && <div style={{ color: "#e53e3e", marginBottom: "10px" }}>{error}</div>}

            {isLoading ? <LoadingSpinnerImage /> : (
                items.length === 0 ? <div className="watchlists-empty">No systematic reviews linked yet.</div> : (
                    <div style={{ overflowX: "auto" }}>
                        <table className="watchlists-items-table">
                            <thead>
                                <tr>
                                    <th style={{ minWidth: "40%" }}>Name</th>
                                    <th style={{ width: "130px" }}>Review Mode</th>
                                    <th style={{ width: "160px" }}>Category</th>
                                    <th style={{ width: "90px" }}>Responsible</th>
                                    <th style={{ width: "70px" }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((sr) => {
                                    const nameStyle = getNameStyle(sr);
                                    const responsible = getResponsible(sr);
                                    const categoryName = getCategoryName(sr.Type);
                                    return (
                                        <tr key={sr.Id}>
                                            <td>
                                                <div style={{ display: "flex", alignItems: "center", wordBreak: "break-word" }}>
                                                    <a href={getSrLink(sr)} style={{ color: nameStyle.color, fontWeight: 500 }}>{sr.Title || `SR #${sr.Id}`}</a>
                                                    {sr.Private === 1 && (
                                                        <i className="fas fa-lock g-ml-5" style={{ color: nameStyle.color, fontSize: "8pt", cursor: "help" }} title={nameStyle.tooltip} />
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <ReviewModeCell mode={sr.Mode} mostRecentContent={sr.MostRecentContent} />
                                            </td>
                                            <td>
                                                <div className="smart_surveillance-category ellipsis" title={categoryName}>{categoryName}</div>
                                            </td>
                                            <td>
                                                <div className={`responsible-person ${responsible.isMe ? "responsible-person_active" : "responsible-person_inactive"}`} title={responsible.name}>
                                                    {(responsible.initials || "—").toUpperCase()}
                                                </div>
                                            </td>
                                            <td>
                                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={(e) => toggleActionMenu(sr.Id, e)} style={{ minWidth: "32px" }}>
                                                    <i className="fa fa-ellipsis-v" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )
            )}

            <RowActionMenu open={openMenuId != null} anchorEl={menuAnchor} onClose={closeActionMenu} minWidth={200}>
                <button type="button" className="watchlists-row-action-menu-item is-danger" onClick={() => unlinkItem(openMenuId)}>
                    Delete from workspace
                </button>
            </RowActionMenu>

            <LinkSystematicReviewsPopup
                isOpen={isLinkPopupOpen}
                onClose={() => setIsLinkPopupOpen(false)}
                workspaceId={workspaceId}
                linkedIds={items.map((item) => item.Id)}
                onLinked={loadItems}
            />
        </div>
    );
};

export default SystematicReviewsTab;
